import { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  Space,
  message,
  Popconfirm,
  Tag,
  Flex,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import axiosInstance from "../axios";

const { Option } = Select;

const MenuConfig = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();
  const [parentOptions, setParentOptions] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/v1/header-menu?platform=all");
      if (response.data?.is_success) {
        const treeData = addKeysToTree(response.data.data);
        setData(treeData);
        // Build flat parent options (only dropdowns and sub-dropdowns)
        const flat = flattenForOptions(treeData);
        setParentOptions(flat.filter((item) => item.type === "dropdown" || item.type === "sub-dropdown"));
      }
    } catch (error) {
      message.error("Failed to fetch menu items");
    }
    setLoading(false);
  };

  // Add 'key' to each node so antd Table can track rows
  // Remove empty children arrays so leaf nodes don't show expand icon
  const addKeysToTree = (items) => {
    return items.map((item) => {
      const node = { ...item, key: item.id };
      if (node.children && node.children.length > 0) {
        node.children = addKeysToTree(node.children);
      } else {
        delete node.children;
      }
      return node;
    });
  };

  // Flatten tree into a flat array (for parent dropdown options)
  const flattenForOptions = (items) => {
    let result = [];
    items.forEach((item) => {
      result.push(item);
      if (item.children && item.children.length > 0) {
        result = result.concat(flattenForOptions(item.children));
      }
    });
    return result;
  };

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({
      type: "nav",
      group_name: "main",
      platform: "all",
      sort_order: 0,
      is_active: true,
      is_highlighted: false,
      open_in_new_tab: false,
    });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      label: record.label,
      url: record.url,
      type: record.type,
      parent_id: record.parent_id,
      group_name: record.group_name,
      sort_order: record.sort_order,
      is_active: Boolean(record.is_active),
      is_highlighted: Boolean(record.is_highlighted),
      open_in_new_tab: Boolean(record.open_in_new_tab),
      icon: record.icon,
      platform: record.platform,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/v1/cre/header-menu/${id}`);
      message.success("Menu item deactivated");
      fetchData();
    } catch (error) {
      message.error("Failed to delete menu item");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        await axiosInstance.put(`/v1/cre/header-menu/${editingItem.id}`, values);
        message.success("Menu item updated");
      } else {
        await axiosInstance.post("/v1/cre/header-menu", values);
        message.success("Menu item created");
      }
      setModalOpen(false);
      fetchData();
    } catch (error) {
      if (error.errorFields) return; // form validation error
      message.error("Failed to save menu item");
    }
  };

  const columns = [
    {
      title: "Label",
      dataIndex: "label",
      key: "label",
    },
    {
      title: "URL",
      dataIndex: "url",
      key: "url",
      ellipsis: true,
      width: 200,
      render: (url) => url || <span style={{ color: "#999" }}>—</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (type) => {
        const colors = { nav: "blue", dropdown: "green", "sub-dropdown": "orange" };
        return <Tag color={colors[type]}>{type}</Tag>;
      },
    },
    {
      title: "Platform",
      dataIndex: "platform",
      key: "platform",
      width: 100,
      render: (platform) => {
        const colors = { all: "purple", tutor: "cyan", student: "magenta", jobs: "gold" };
        return <Tag color={colors[platform]}>{platform}</Tag>;
      },
    },
    {
      title: "Group",
      dataIndex: "group_name",
      key: "group_name",
      width: 120,
    },
    {
      title: "Order",
      dataIndex: "sort_order",
      key: "sort_order",
      width: 70,
      align: "center",
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      width: 70,
      align: "center",
      render: (val) => (val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag>),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Popconfirm
            title="Deactivate this menu item?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div
      style={{
        padding: "20px",
        width: "100%",
        height: "100%",
        overflow: "auto",
        boxSizing: "border-box",
      }}
    >
      <Flex justify="space-between" align="center" wrap="wrap" gap={8} style={{ marginBottom: "16px" }}>
        <h2 style={{ margin: 0 }}>Menu Configuration</h2>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={fetchData}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add Menu Item
          </Button>
        </Space>
      </Flex>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        bordered
        scroll={{ x: 900 }}
      />

      <Modal
        title={editingItem ? "Edit Menu Item" : "Add Menu Item"}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingItem ? "Update" : "Create"}
        width="90%"
        style={{ maxWidth: 600 }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="label" label="Label" rules={[{ required: true, message: "Label is required" }]}>
            <Input placeholder="e.g. Home, Our Services" />
          </Form.Item>

          <Form.Item name="url" label="URL">
            <Input placeholder="e.g. / or https://example.com" />
          </Form.Item>

          <Flex gap={16} wrap="wrap">
            <Form.Item name="type" label="Type" rules={[{ required: true }]} style={{ flex: 1, minWidth: 200 }}>
              <Select>
                <Option value="nav">Nav (Link)</Option>
                <Option value="dropdown">Dropdown</Option>
                <Option value="sub-dropdown">Sub-Dropdown</Option>
              </Select>
            </Form.Item>

            <Form.Item name="platform" label="Platform" rules={[{ required: true }]} style={{ flex: 1, minWidth: 200 }}>
              <Select>
                <Option value="all">All</Option>
                <Option value="tutor">Tutor</Option>
                <Option value="student">Student</Option>
                <Option value="jobs">Jobs</Option>
              </Select>
            </Form.Item>
          </Flex>

          <Flex gap={16} wrap="wrap">
            <Form.Item name="parent_id" label="Parent Item" style={{ flex: 1, minWidth: 200 }}>
              <Select allowClear placeholder="None (top-level)">
                {parentOptions.map((item) => (
                  <Option key={item.id} value={item.id}>
                    {item.label} ({item.platform})
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="group_name"
              label="Group Name"
              rules={[{ required: true }]}
              style={{ flex: 1, minWidth: 200 }}
            >
              <Select>
                <Option value="main">Main</Option>
                <Option value="our_services">Our Services</Option>
                <Option value="browse">Browse</Option>
                <Option value="signin">Sign In</Option>
                <Option value="register">Register</Option>
              </Select>
            </Form.Item>
          </Flex>

          <Flex gap={16} wrap="wrap">
            <Form.Item
              name="sort_order"
              label="Sort Order"
              rules={[{ required: true }]}
              style={{ flex: 1, minWidth: 200 }}
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item name="icon" label="Icon" style={{ flex: 1, minWidth: 200 }}>
              <Input placeholder="e.g. header-arrow.svg" />
            </Form.Item>
          </Flex>

          <Flex gap={24} wrap="wrap">
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item name="is_highlighted" label="Highlighted" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item name="open_in_new_tab" label="Open in New Tab" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Flex>
        </Form>
      </Modal>
    </div>
  );
};

export default MenuConfig;
